"use strict"

import ErrorDialogController from "../error-dialog/error-dialog.controller"
import Validation from "../../model/util/validation"
import InviteCoTeacherDialogTemplate from "./invite-co-teacher-dialog.html"
import AnalyticsService from "../../services/analytics/analytics.service"
import CoTeacherService from "../../services/co-teacher/co-teacher.service"
import AuthService from "../../services/auth/auth.service"

export class InviteCoTeacherDialogStates {
  static get INVITE_TEACHER() {
    return "invite-teacher"
  }

  static get LOADING() {
    return "loading"
  }
}

export default class InviteCoTeacherDialogController {
  /**
   * @param {AnalyticsService} AnalyticsService
   * @param {CoTeacherService} CoTeacherService
   * @param {AuthService} AuthService
   */
  constructor(
    $mdDialog,
    $mdToast,
    $q,
    $location,
    AnalyticsService,
    CoTeacherService,
    AuthService,
    rosterName,
    rosterId,
    teacherName,
  ) {
    "ngInject"

    this.$q = $q
    this.$mdDialog = $mdDialog
    this.$mdToast = $mdToast
    this.$location = $location

    /** @type {CoTeacherService} */
    this._coTeacherService = CoTeacherService
    /** @type {AuthService} */
    this._authService = AuthService

    this._errorDialog = ErrorDialogController.show

    this.emailRegex = Validation.EmailRegex

    this.rosterName = rosterName
    this.rosterId = rosterId
    this.teacherName = teacherName
    this.state = InviteCoTeacherDialogStates.INVITE_TEACHER
    this.emails = ""
    this.error = false
    this.mdrRecipientList = []
    this.linkBuilder(this.rosterId)

    this.inviteCoTeacherMessageBody = this.inviteCoTeacherEmailBody(
      this.rosterName,
      this.teacherName,
    )
  }

  /**
   * show the dialog
   * @param  {$mdDialog}  $mdDialog
   * @param  {assignment} assignment
   * @param  {string} rosterName
   * @param  {string} rosterId
   * @param  {string} teacherName
   * @return {Promise}
   */
  static show($mdDialog, rosterName, rosterId, teacherName) {
    let config = {
      controller: InviteCoTeacherDialogController,
      template: InviteCoTeacherDialogTemplate,
      controllerAs: "ctrl",
      autoWrap: false,
      clickOutsideToClose: true,
      focusOnOpen: false,
      locals: {
        rosterName,
        rosterId,
        teacherName,
      },
    }

    return $mdDialog.show(config)
  }

  /**
   * builds share link - invites coteacher to a roster
   * @param rosterId {string}
   */
  linkBuilder(rosterId) {
    let host = this.$location.host()
    let port = ""
    // If we're not on the default http or https ports, we'll include the port in the link
    if (!(this.$location.port() === 80 || this.$location.port() === 443)) {
      port = `:${this.$location.port()}`
    }

    let path = `/account/rosters/${rosterId}`
    this._link = `${host}${port}${path}`
  }

  inviteCoTeacherReferralUrl(rosterName) {
    let shareReferralLink = `https://${this._link}?utm_source=${this._authService.currentUserId}`
    let shareReferralLinkHtml = `<p>Join <a href="${shareReferralLink}">${rosterName}</a></p>`
    return shareReferralLinkHtml
  }

  inviteCoTeacherEmailBody(rosterName, teacherName) {
    let defaultBody = `Hello,\n${teacherName} wants to co-teach with you in Classkick! Login or sign up with this email using the link below to get started!\nView https://${this._link}\nOn Classkick you see students' thinking instantly, save hours of time, and ensure learners are engaged getting help and feedback. This invite gives you access to ${rosterName} where you can co-teach and give feedback, appearing as ${teacherName}.\nCheck it out!`

    return defaultBody
  }

  inviteCoTeacherEmailBodyHtml(rosterName, teacherName) {
    let htmlBody = `
      <p>Hello,</p>
      <p><strong>${teacherName}</strong> wants to co-teach with you in Classkick! Login or sign up with this email using the link below to get started!</p>
      <br>${this.inviteCoTeacherReferralUrl(rosterName)}<br>
      <p>On Classkick you see students' thinking instantly, save hours of time, and ensure learners are engaged getting help and feedback. This invite gives you access to <strong>${rosterName}</strong> where you can co-teach and give feedback, appearing as <strong>${teacherName}</strong>.</p>
      <p>Check it out!</p>
    `
    return htmlBody
  }

  /**
   * tests whether email is valid
   * @return {Boolean} whether the email is valid or not
   */
  isEmailValid(email) {
    return this.emailRegex.test(email.trim())
  }

  get isEmailBodyValid() {
    if (this.inviteCoTeacherMessageBody !== "") {
      return true
    }
    return false
  }

  /**
   *
   * @param text {string}
   * @returns html converted text
   */
  convertCustomEmailBodyTextToHtml(text) {
    let link = `https://${this._link}`

    // Escape special regex characters in the link
    let escapedLink = link.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")

    // Create a regex pattern to match "View " followed by the exact link
    let viewLinkPattern = new RegExp("View " + escapedLink, "g")

    // First, remove any "View [link]" patterns
    // We will adding back a html friendly link
    let cleanedText = text.replace(viewLinkPattern, "")

    // Then convert newlines to <br> tags
    let textWithBrTags = cleanedText.replace(/\n/g, "<br>")

    return textWithBrTags
  }

  /**
   *
   * @param assignmentName {string}
   * @returns body of the email, if default then use defaultHtml else append share url with referral link.
   */
  getEmailBodyForCoTeacherInvite(rosterName, teacherName) {
    if (
      this.inviteCoTeacherMessageBody ===
      this.inviteCoTeacherEmailBody(rosterName, teacherName)
    ) {
      this.inviteCoTeacherMessageBody = this.inviteCoTeacherEmailBodyHtml(
        rosterName,
        teacherName,
      )
    } else {
      this.inviteCoTeacherMessageBody = `${this.convertCustomEmailBodyTextToHtml(this.inviteCoTeacherMessageBody)}${this.inviteCoTeacherReferralUrl(rosterName)}`
    }
    return this.inviteCoTeacherMessageBody
  }

  /**
   * tests list of emails against the list of emails regex.
   * @return {Boolean} whether the email list is valid or not
   */
  get isEmailListValid() {
    let emails = this.emails.split(/[,;]/)

    emails = emails.concat(this.mdrRecipientList).filter((email) => email)
    let isValid = false
    for (let email of emails) {
      isValid = this.isEmailValid(email)
      if (!isValid) return false
    }

    return true
  }

  /**
   * tests if manually entered emails are below a threshold as a security measure.
   * @return {Boolean} whether the email list is below threshold
   */
  get isInputEmailsBelowLimit() {
    let emails = this.emails.split(/[,;]/)
    if (emails.length > 10) {
      return false
    }

    return true
  }

  get isInviteTeacher() {
    return this.state === InviteCoTeacherDialogStates.INVITE_TEACHER
  }

  get isLoading() {
    return this.state === InviteCoTeacherDialogStates.LOADING
  }

  /**
   * set validity of email input
   */
  _displayToastMessage(message) {
    this._toastMessage = message
    let toastClassName = "referral-toast"
    const config = this.$mdToast
      .simple(this._toastMessage)
      .action(" ")
      .actionKey("x")
      .position("top right")
      .toastClass(toastClassName)
      .hideDelay(4000)
    this.$mdToast.show(config)
  }

  /**
   * close the dialog. rejects the promise returned from the show method
   */
  cancel() {
    this.$mdDialog.cancel()
  }

  /**
   * set validity of email input
   * @return {Boolean}
   */
  setEmailValidityForCoTeacher(scope) {
    scope.emailInviteCoTeacherForm.email.$setValidity(
      "max-emails",
      this.isInputEmailsBelowLimit,
    )
    scope.emailInviteCoTeacherForm.email.$setValidity(
      "pattern",
      this.isEmailListValid,
    )
  }

  setEmailBodyValidityForCoTeacher(scope) {
    scope.emailInviteCoTeacherForm.bodyInput.$setValidity(
      "pattern",
      this.isEmailBodyValid,
    )
  }

  /**
   * Sends email invite to teacher
   */
  async sendEmailToCoTeachers() {
    try {
      let errorMessages = []
      if (!this.isInputEmailsBelowLimit) return
      if (!this.isEmailListValid) return
      if (!this.isEmailBodyValid) return

      this.state = InviteCoTeacherDialogStates.LOADING

      let emailList = this.emails.split(/[,;]/)
      emailList = emailList
        .concat(this.mdrRecipientList)
        .filter((email) => email)

      this.inviteCoTeacherMessageBody = this.getEmailBodyForCoTeacherInvite(
        this.rosterName,
        this.teacherName,
      )

      const promises = emailList.map(async (email) => {
        try {
          await this._coTeacherService.inviteCoTeacher(
            this.rosterId,
            email.trim().toLowerCase(),
            this.inviteCoTeacherMessageBody,
          )
          return { success: true }
        } catch (error) {
          let errorMessage =
            "Please try again later or send an email to support@classkick.com"
          switch (error.message) {
            case "Found an account with the email address but they are not a teacher.":
              errorMessage = `You entered an email - ${email} that cannot be invited to co-teach. Please try a different email.`
              break
            case "Cannot assign self as coteacher to owned roster.":
              errorMessage = `You cannot invite yourself - ${email} as a co-teacher`
              break
          }
          return { success: false, message: errorMessage }
        }
      })

      const results = await this.$q.all(promises)

      this.state = InviteCoTeacherDialogStates.INVITE_TEACHER
      this.$mdDialog.hide()

      // Collect all error messages
      errorMessages = results
        .filter((result) => !result.success)
        .map((result) => result.message)

      if (errorMessages.length) {
        this._errorDialog(
          this.$mdDialog,
          "Uh oh! An error occurred",
          errorMessages.join("\n"),
        )
      } else {
        this._displayToastMessage("Your invite has been sent!")
      }
    } catch (error) {
      this.state = InviteCoTeacherDialogStates.INVITE_TEACHER
      this._errorDialog(
        this.$mdDialog,
        "Uh oh! An error occurred",
        "Please try again later or send an email to support@classkick.com",
      )
    }
  }
}
